/**
 * VoicePlanBoard.jsx — To-do list view for a Voice Plan
 *
 * Shows grouped voice notes as an actionable task list.
 * - Token code (VP-XXXX) prominent + quick-copy for Copilot reference
 * - AI analysis panel (summary, critical analysis, recommended sequence)
 * - Active tasks with Done button -> prompts for short completion note
 * - Completed tasks log at bottom with timestamps + done notes
 * - Copy Plan Prompt for pasting into Copilot
 */

import { useState, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, IconButton, CircularProgress,
  Stack, Tooltip, Alert, Paper, Button, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, Collapse, Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  AutoAwesome as AiIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  CheckCircle as DoneIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  RadioButtonUnchecked as TodoIcon,
  RocketLaunch as ImplementIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useVoicePlan, useUpdatePlanTask, useDeletePlanTask,
  useAddPlanTask, useSummarizePlan, useUpdateVoicePlan,
  useCompletePlanTask, useQueuePlan,
} from '../hooks/useVoicePlans';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const PRIORITY_COLORS = {
  high: '#EF4444',
  medium: '#F59E0B',
  low: '#6B7280',
};

/* -- Active Task Card -- */
function TaskItem({ task, planId, onComplete }) {
  const deleteTask = useDeletePlanTask();
  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      variant="outlined"
      sx={{ mb: 1, borderLeft: '3px solid #3B82F6', '&:hover': { bgcolor: 'action.hover' } }}
    >
      <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TodoIcon sx={{ fontSize: 18, color: '#3B82F6', flexShrink: 0 }} />
          <Typography
            variant="body2"
            sx={{ flex: 1, fontWeight: 500, fontSize: '0.85rem', lineHeight: 1.4, cursor: 'pointer' }}
            onClick={() => setExpanded(!expanded)}
          >
            {task.title}
          </Typography>
          <Tooltip title="Mark done">
            <IconButton
              size="small"
              onClick={() => onComplete(task)}
              sx={{ color: '#22C55E', '&:hover': { bgcolor: '#22C55E18' } }}
            >
              <DoneIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove">
            <IconButton
              size="small"
              onClick={() => { if (window.confirm('Remove this task?')) deleteTask.mutate({ planId, taskId: task.id }); }}
              sx={{ color: 'error.main', opacity: { xs: 0.85, sm: 0.3 }, p: { xs: 0.75, sm: 0.625 }, '&:hover': { opacity: 1 } }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {task.description && (
          <Collapse in={expanded}>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5, mt: 0.25, display: 'block' }}>
              {task.description}
            </Typography>
          </Collapse>
        )}
        {task.voicenote_id && (
          <Chip label="linked" size="small" sx={{ fontSize: '0.6rem', height: 16, ml: 3.5, mt: 0.5 }} />
        )}
      </CardContent>
    </Card>
  );
}

/* -- Completed Task Row -- */
function DoneItem({ task }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.5 }}>
      <DoneIcon sx={{ fontSize: 16, color: '#22C55E', mt: 0.25, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontSize: '0.82rem', textDecoration: 'line-through', color: 'text.secondary' }}>
          {task.title}
        </Typography>
        {task.done_note && (
          <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', display: 'block' }}>
            {task.done_note}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
        {timeAgo(task.completed_at)}
      </Typography>
    </Box>
  );
}

/* -- AI Analysis Panel -- */
function AnalysisPanel({ plan }) {
  const [expanded, setExpanded] = useState(true);
  let analysis = null;
  try {
    analysis = typeof plan.summary === 'string' ? JSON.parse(plan.summary) : plan.summary;
  } catch { /* not JSON */ }

  if (!analysis) return null;

  return (
    <Paper variant="outlined" sx={{ mb: 2, p: 2, borderColor: '#A78BFA44', borderLeft: '3px solid #A78BFA' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <AiIcon sx={{ color: '#A78BFA', fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>AI Analysis</Typography>
        {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
      </Box>
      <Collapse in={expanded}>
        {analysis.summary && (
          <Typography variant="body2" sx={{ mt: 1, mb: 1.5 }}>{analysis.summary}</Typography>
        )}
        {analysis.critical_analysis && (
          <Alert severity="warning" sx={{ mb: 1.5, fontSize: '0.82rem' }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Critical Analysis</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{analysis.critical_analysis}</Typography>
          </Alert>
        )}
        {analysis.todo_list?.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
              AI Suggested Tasks
            </Typography>
            {analysis.todo_list.map((a, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 0.75 }}>
                <Chip
                  label={a.priority}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.65rem', fontWeight: 600,
                    bgcolor: (PRIORITY_COLORS[a.priority] || '#6B7280') + '22',
                    color: PRIORITY_COLORS[a.priority] || '#6B7280',
                  }}
                />
                {a.effort && (
                  <Chip label={a.effort} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.6rem' }} />
                )}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>{a.title}</Typography>
                  {a.rationale && <Typography variant="caption" color="text.secondary">{a.rationale}</Typography>}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {!analysis.todo_list?.length && analysis.action_items?.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
              Action Items
            </Typography>
            {analysis.action_items.map((a, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 0.75 }}>
                <Chip
                  label={a.priority}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.65rem', fontWeight: 600,
                    bgcolor: (PRIORITY_COLORS[a.priority] || '#6B7280') + '22',
                    color: PRIORITY_COLORS[a.priority] || '#6B7280',
                  }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>{a.title}</Typography>
                  {a.rationale && <Typography variant="caption" color="text.secondary">{a.rationale}</Typography>}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {analysis.risks?.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Risks</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
              {analysis.risks.map((r, i) => (
                <Typography key={i} component="li" variant="body2" sx={{ fontSize: '0.82rem', color: 'error.main' }}>{r}</Typography>
              ))}
            </Box>
          </Box>
        )}
        {analysis.recommended_sequence && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Sequence</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.82rem' }}>{analysis.recommended_sequence}</Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}

/* -- Main Component -- */
export default function VoicePlanBoard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: plan, isLoading } = useVoicePlan(id);
  const summarize = useSummarizePlan();
  const updatePlan = useUpdateVoicePlan();
  const addTask = useAddPlanTask();
  const completeTask = useCompletePlanTask();
  const queuePlanMutation = useQueuePlan();

  const [completingTask, setCompletingTask] = useState(null);
  const [doneNote, setDoneNote] = useState('');
  const [addingTask, setAddingTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [queued, setQueued] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [showDoneLog, setShowDoneLog] = useState(true);

  const { activeTasks, doneTasks } = useMemo(() => {
    if (!plan?.tasks) return { activeTasks: [], doneTasks: [] };
    const active = plan.tasks.filter((t) => t.lane !== 'done');
    const done = plan.tasks.filter((t) => t.lane === 'done');
    return { activeTasks: active, doneTasks: done };
  }, [plan]);

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress size={32} /></Box>;
  }

  if (!plan) {
    return (
      <Box sx={{ textAlign: 'center', pt: 8 }}>
        <Typography color="text.secondary">Plan not found</Typography>
        <Button onClick={() => navigate('/voicenotes')} sx={{ mt: 2 }}>Back to Voice Notes</Button>
      </Box>
    );
  }

  const planCode = plan.code;

  const copyCode = () => {
    navigator.clipboard.writeText(planCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 1500);
  };

  const handleComplete = () => {
    if (!completingTask) return;
    completeTask.mutate(
      { planId: plan.id, taskId: completingTask.id, doneNote: doneNote.trim() || null },
      { onSuccess: () => { setCompletingTask(null); setDoneNote(''); } }
    );
  };

  const handleAddTask = () => {
    if (!newTaskTitle.trim()) return;
    addTask.mutate({ planId: plan.id, title: newTaskTitle.trim(), lane: 'backlog' });
    setNewTaskTitle('');
    setAddingTask(false);
  };

  const isQueued = !!plan.queued_at || queued;

  const handleQueue = () => {
    if (!isQueued) {
      queuePlanMutation.mutate(plan.id);
      setQueued(true);
    }
  };

  const progress = plan.tasks?.length ? Math.round((doneTasks.length / plan.tasks.length) * 100) : 0;

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <IconButton onClick={() => navigate('/voicenotes')} size="small"><BackIcon /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>{plan.title}</Typography>
        <Tooltip title={codeCopied ? 'Copied!' : 'Copy token'}>
          <Chip
            label={planCode}
            icon={codeCopied ? <CheckIcon sx={{ fontSize: '16px !important' }} /> : <CopyIcon sx={{ fontSize: '14px !important' }} />}
            onClick={copyCode}
            clickable
            sx={{
              fontFamily: 'monospace', fontWeight: 700, fontSize: '0.95rem',
              bgcolor: '#13B5EA22', color: '#13B5EA',
              '& .MuiChip-icon': { color: '#13B5EA' },
              '&:hover': { bgcolor: '#13B5EA38' },
            }}
          />
        </Tooltip>
      </Box>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 2, ml: 5 }}>
        Paste <code style={{ background: '#1e293b', color: '#22d3ee', padding: '1px 6px', borderRadius: 4 }}>{planCode}</code> in Copilot to reference this plan
      </Typography>

      {/* Progress bar */}
      {plan.tasks?.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {doneTasks.length}/{plan.tasks.length} done
            </Typography>
            <Typography variant="caption" color="text.disabled">({progress}%)</Typography>
          </Box>
          <Box sx={{ height: 4, bgcolor: 'action.hover', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${progress}%`, bgcolor: '#22C55E', borderRadius: 2, transition: 'width 300ms' }} />
          </Box>
        </Box>
      )}

      {/* AI Analysis */}
      <AnalysisPanel plan={plan} />

      {/* Action buttons */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          size="small"
          startIcon={summarize.isPending ? <CircularProgress size={14} /> : <AiIcon />}
          onClick={() => summarize.mutate(plan.id)}
          disabled={summarize.isPending || activeTasks.length === 0}
          sx={{ textTransform: 'none', fontSize: '0.8rem' }}
        >
          {summarize.isPending ? 'Analyzing...' : 'AI Analyze'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={isQueued ? <CheckIcon /> : <ImplementIcon />}
          onClick={handleQueue}
          disabled={isQueued}
          sx={{
            textTransform: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            borderColor: isQueued ? '#22C55E' : '#F97316',
            color: isQueued ? '#22C55E' : '#F97316',
            bgcolor: isQueued ? '#22C55E12' : '#F9731612',
            '&:hover': { bgcolor: isQueued ? '#22C55E22' : '#F9731622', borderColor: isQueued ? '#22C55E' : '#F97316' },
          }}
        >
          {isQueued ? 'Queued for Copilot' : 'Implement'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddingTask(true)}
          sx={{ textTransform: 'none', fontSize: '0.8rem' }}
        >
          Add Task
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          color="error"
          onClick={() => {
            if (window.confirm(`Archive plan "${plan.title}"?`)) {
              updatePlan.mutate({ id: plan.id, status: 'archived' });
              navigate('/voicenotes');
            }
          }}
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
        >
          Archive
        </Button>
      </Stack>

      {summarize.isError && (
        <Alert severity="error" sx={{ mb: 2, fontSize: '0.82rem' }}>{summarize.error?.message || 'Analysis failed'}</Alert>
      )}

      {/* Active Tasks */}
      {activeTasks.length > 0 ? (
        <>
          <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
            To Do ({activeTasks.length})
          </Typography>
          {activeTasks.map((t) => (
            <TaskItem key={t.id} task={t} planId={plan.id} onComplete={(task) => setCompletingTask(task)} />
          ))}
        </>
      ) : (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', mb: 2, borderStyle: 'dashed' }}>
          <DoneIcon sx={{ fontSize: 32, color: '#22C55E', mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">All tasks complete!</Typography>
        </Paper>
      )}

      {/* Completion Log */}
      {doneTasks.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mb: 1 }}
            onClick={() => setShowDoneLog(!showDoneLog)}
          >
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Done ({doneTasks.length})
            </Typography>
            {showDoneLog ? <CollapseIcon sx={{ fontSize: 16, color: 'text.disabled' }} /> : <ExpandIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
          </Box>
          <Collapse in={showDoneLog}>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover', borderColor: '#22C55E33' }}>
              {doneTasks.map((t) => <DoneItem key={t.id} task={t} />)}
            </Paper>
          </Collapse>
        </Box>
      )}

      {/* Complete Task Dialog */}
      <Dialog open={!!completingTask} onClose={() => setCompletingTask(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', pb: 0.5 }}>
          <DoneIcon sx={{ color: '#22C55E', fontSize: 20, mr: 1, verticalAlign: 'middle' }} />
          Done: {completingTask?.title}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="What was done? (optional)"
            placeholder="Quick note - e.g. 'Added to jobs page', 'Sent email', 'Deployed fix'..."
            multiline
            minRows={2}
            maxRows={4}
            value={doneNote}
            onChange={(e) => setDoneNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComplete(); } }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompletingTask(null)}>Cancel</Button>
          <Button onClick={handleComplete} variant="contained" color="success" disabled={completeTask.isPending}>
            {completeTask.isPending ? 'Saving...' : 'Mark Done'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={addingTask} onClose={() => setAddingTask(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem' }}>Add Task</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Task title"
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddTask()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddingTask(false)}>Cancel</Button>
          <Button onClick={handleAddTask} variant="contained" disabled={!newTaskTitle.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
